import { useState } from 'react';
import { Card, SegmentedControl } from '@salonomy/ui';
import { useProducts } from '../data/stock.js';
import {
  MovementsPanel,
  ProductsPanel,
  StockLevelsPanel,
  SuppliersPanel,
} from '../features/stock/index.js';
import { useAuth } from '../lib/auth.js';

type Tab = 'catalogue' | 'levels' | 'movements' | 'suppliers';

const TABS: Array<{ value: Tab; label: string }> = [
  { value: 'catalogue', label: 'Catalogue' },
  { value: 'levels', label: 'Levels' },
  { value: 'movements', label: 'Movements' },
  { value: 'suppliers', label: 'Suppliers' },
];

/**
 * WS7 stock module page — Catalogue | Levels | Movements | Suppliers, on the standard screen-scroll /
 * screen-pad / topbar layout. Self-gated to managers: every stock WRITE is requireManager+stock:write
 * on the API, so a non-manager gets an honest note rather than a guaranteed 403. The Movements panel
 * needs a product list for its name lookup + selector, so the page fetches the catalogue once (the
 * first page) and hands the rows down.
 */
export function StockPage() {
  const { isManager } = useAuth();
  const [tab, setTab] = useState<Tab>('catalogue');

  // A product list for the movements panel's name-lookup + the movement form's selector. Page one is
  // enough for the picker; the panels that page heavily (catalogue, ledger) fetch their own data.
  const { data: productsPage } = useProducts({}, 0);
  const products = productsPage?.data ?? [];

  if (!isManager) {
    return (
      <>
        <div className="topbar">
          <div className="topbar__title">Stock</div>
        </div>
        <div className="screen-scroll">
          <div
            className="screen-pad"
            style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 980 }}
          >
            <Card>
              <div className="section-title">Stock is manager-only</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
                Products, suppliers and stock movements are managed by an owner or manager. Ask one
                of them to add stock, record a delivery, or adjust a count. You can still sell
                retail products at the till as normal.
              </div>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Stock</div>
        </div>
        <div className="topbar__right">
          <SegmentedControl options={TABS} value={tab} onChange={setTab} />
        </div>
      </div>

      <div className="screen-scroll">
        <div
          className="screen-pad"
          style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 980 }}
        >
          {tab === 'catalogue' ? <ProductsPanel /> : null}
          {tab === 'levels' ? <StockLevelsPanel /> : null}
          {tab === 'movements' ? <MovementsPanel products={products} /> : null}
          {tab === 'suppliers' ? <SuppliersPanel /> : null}
        </div>
      </div>
    </>
  );
}
